import {gsap} from '../../../vendors/gsap';
import getDOMfromString from '../../../helpers/getDOMfromString';
import onAppend from '../../../helpers/onAppend';
import getSlideIndexes from '../../../helpers/getSlideIndexes';

/**
 * This method creates and returns text carousel
 *
 * @param {Object} config - options to customize carousel
 * @returns {Object} - slide up and down methods
 */
const createTextCarousel = config => {
    const {storyData} = config;
    const data = Array.isArray(storyData) ? storyData : [];
    const slideCount = data.length - 1;
    let titleSlides = [];
    let detailSlides = [];
    let activeIndex = 0;
    let nextIndex = 1;
    let prevIndex = slideCount;
    let upTl = null;
    let downTl = null;

    /**
     * Update title for screen readers
     *
     * @param {string} title - title to set
     */
    function updateTitle(title) {
        if (!title.trim()) return;

        document.querySelector('.sr-story-title').textContent = title;
    }

    /**
     * This function will generate the html for carousel
     */
    function generateCarousel() {
        let titleStr = '';
        let detailStr = '';

        data.forEach((item, index) => {
            titleStr += `<div class="story-title ${index === 0 ? 'first' : ''}"><h1>${item?.name ?? ''}</h1></div>`;
            detailStr += `<div class="story-details ${index === 0 ? 'first' : ''}">
            <div class="story-year">${item?.year ?? ''}</div>
            <div class="story-description">${item?.credits ?? ''}</div>
            </div>`;
        });
        titleStr = `<div class="story-title-container">${titleStr}</div>`;
        detailStr = `<div class="story-year-container">${detailStr}</div>`;

        const container = document.querySelector('.story-details-container');
        container.append(getDOMfromString(titleStr));

        // setting initial height
        const observer = onAppend(container, () => {
            titleSlides = gsap.utils.toArray('.story-title');
            detailSlides = gsap.utils.toArray('.story-details');

            gsap.set(titleSlides[activeIndex], {autoAlpha: 1, y: 0});
            gsap.set(detailSlides[activeIndex], {autoAlpha: 1, y: 0});

            observer.disconnect();
        });
        container.append(getDOMfromString(detailStr));

        updateTitle(`${data[0]?.name ?? ''} ${data[0]?.year ?? ''}`);
    }

    /**
     *
     * @param {Object} animationConfig - animation options
     * @returns {Object} - gsap timeline object
     */
    function slideUpAnimation(animationConfig) {
        const {toRemove, toActivate, animateYear = true} = animationConfig;
        const slideToRemoveHeight = titleSlides[toRemove].offsetHeight;
        const slideToActivateHeight = titleSlides[toActivate].offsetHeight;

        if (!animateYear) {
            return gsap
                .timeline()
                .to(titleSlides[toRemove], {
                    y: '-100%',
                    duration: 0.4,
                    ease: 'power1.in'
                })
                .add(() => {
                    gsap.set('.story-title-container', {
                        height: slideToActivateHeight
                    });
                    gsap.set(titleSlides[toActivate], {autoAlpha: 1});
                    gsap.set(titleSlides[toRemove], {autoAlpha: 0});
                })
                .fromTo(
                    titleSlides[toActivate],
                    {y: slideToRemoveHeight},
                    {
                        y: 0,
                        duration: 0.4,
                        ease: 'power1.out'
                    },
                    '<'
                );
        }

        const toRemoveDetailHeight = detailSlides[toRemove].offsetHeight;
        const toActivateDetailHeight = detailSlides[toActivate].offsetHeight;

        return gsap
            .timeline()
            .to(titleSlides[toRemove], {
                y: '-100%',
                duration: 0.4,
                ease: 'power1.in'
            })
            .to(
                detailSlides[toRemove],
                {
                    y: '-100%',
                    duration: 0.4,
                    ease: 'power1.in'
                },
                '<'
            )
            .add(() => {
                // title carousel
                gsap.set('.story-title-container', {
                    height: slideToActivateHeight
                });
                gsap.set(titleSlides[toActivate], {autoAlpha: 1});
                gsap.set(titleSlides[toRemove], {autoAlpha: 0});

                // year carousel
                gsap.set('.story-year-container', {
                    height: toActivateDetailHeight
                });
                gsap.set(detailSlides[toActivate], {autoAlpha: 1});
                gsap.set(detailSlides[toRemove], {autoAlpha: 0});
            })
            .fromTo(
                titleSlides[toActivate],
                {y: slideToRemoveHeight},
                {
                    y: 0,
                    duration: 0.4,
                    ease: 'power1.out'
                },
                '<'
            )
            .fromTo(
                detailSlides[toActivate],
                {y: toRemoveDetailHeight},
                {
                    y: 0,
                    duration: 0.4,
                    ease: 'power1.out'
                },
                '<'
            );
    }

    /**
     *
     * @param {Object} animationConfig - animation options
     * @returns {Object} - gsap timeline object
     */
    function slideDownAnimation(animationConfig) {
        const {toRemove, toActivate, animateYear = true} = animationConfig;
        const slideToRemoveHeight = titleSlides[toRemove].offsetHeight;
        const slideToActivateHeight = titleSlides[toActivate].offsetHeight;

        if (!animateYear) {
            return gsap
                .timeline()
                .to(titleSlides[toRemove], {
                    y: Math.max(slideToActivateHeight, slideToRemoveHeight),
                    duration: 0.4,
                    ease: 'power1.in'
                })
                .add(() => {
                    gsap.set('.story-title-container', {
                        height: slideToActivateHeight
                    });
                    gsap.set(titleSlides[toActivate], {autoAlpha: 1});
                    gsap.set(titleSlides[toRemove], {autoAlpha: 0});
                })
                .fromTo(titleSlides[toActivate], {y: '-100%'}, {y: 0, duration: 0.4, ease: 'power1.out'}, '<');
        }

        const toRemoveDetailHeight = detailSlides[toRemove].offsetHeight;
        const toActivateDetailHeight = detailSlides[toActivate].offsetHeight;

        return gsap
            .timeline()
            .to(titleSlides[toRemove], {
                y: Math.max(slideToActivateHeight, slideToRemoveHeight),
                duration: 0.4,
                ease: 'power1.in'
            })
            .to(
                detailSlides[toRemove],
                {
                    y: Math.max(toActivateDetailHeight, toRemoveDetailHeight),
                    duration: 0.4,
                    ease: 'power1.in'
                },
                '<'
            )
            .add(() => {
                // title
                gsap.set('.story-title-container', {
                    height: slideToActivateHeight
                });
                gsap.set(titleSlides[toActivate], {autoAlpha: 1});
                gsap.set(titleSlides[toRemove], {autoAlpha: 0});

                // details
                gsap.set('.story-year-container', {
                    height: toActivateDetailHeight
                });
                gsap.set(detailSlides[toActivate], {autoAlpha: 1});
                gsap.set(detailSlides[toRemove], {autoAlpha: 0});
            })
            .fromTo(titleSlides[toActivate], {y: '-100%'}, {y: 0, duration: 0.4, ease: 'power1.out'}, '<')
            .fromTo(detailSlides[toActivate], {y: '-100%'}, {y: 0, duration: 0.4, ease: 'power1.out'}, '<');
    }

    /**
     * This function updates slide index
     *
     * @param {Object} newIndexes - params object
     * @returns {undefined}
     */
    function updateSlideIndexes(newIndexes) {
        const {activeSlide, prevSlide, nextSlide} = newIndexes;
        activeIndex = activeSlide;
        prevIndex = prevSlide;
        nextIndex = nextSlide;
    }

    /**
     * This function handles slide up behaviour
     */
    function slideUp() {
        const slideToRemove = activeIndex;
        const slideToActivate = nextIndex;

        // to get the new next slide
        activeIndex += 1;
        updateSlideIndexes(getSlideIndexes({activeIndex, prevIndex, nextIndex, slideCount}));

        if (upTl) {
            upTl.totalProgress(1);
        }

        upTl = slideUpAnimation({
            toRemove: slideToRemove,
            toActivate: slideToActivate
        });

        updateTitle(`${data[activeIndex]?.name ?? ''} ${data[activeIndex]?.year ?? ''}`);
    }

    /**
     * This function handles slide down behaviour
     */
    function slideDown() {
        const slideToRemove = activeIndex;
        const slideToActivate = prevIndex;
        activeIndex -= 1;
        updateSlideIndexes(getSlideIndexes({activeIndex, prevIndex, nextIndex, slideCount}));

        if (downTl) {
            downTl.totalProgress(1);
        }

        downTl = slideDownAnimation({
            toRemove: slideToRemove,
            toActivate: slideToActivate
        });

        updateTitle(`${data[activeIndex]?.name ?? ''} ${data[activeIndex]?.year ?? ''}`);
    }

    /**
     * This function handles slide to specific index
     *
     * @param {number} slideIndex - slide index
     * @param {boolean} complete - if true immediately complete the animation
     */
    function slideTo(slideIndex, complete) {
        if (slideIndex === activeIndex) return;

        const slideToRemove = activeIndex;
        const slideToActivate = slideIndex;

        gsap.set(titleSlides[prevIndex], {y: '-100%'});
        gsap.set(detailSlides[prevIndex], {y: '-100%'});
        gsap.set(titleSlides[nextIndex], {y: '-100%'});
        gsap.set(detailSlides[nextIndex], {y: '-100%'});

        updateSlideIndexes(
            getSlideIndexes({activeIndex: slideToActivate, prevIndex: slideToRemove, nextIndex, slideCount})
        );

        if (upTl) {
            upTl.totalProgress(1);
        }

        upTl = slideUpAnimation({
            toRemove: slideToRemove,
            toActivate: slideToActivate
        });

        if (complete) {
            upTl.totalProgress(1);
        }

        updateTitle(`${data[activeIndex]?.name ?? ''} ${data[activeIndex]?.year ?? ''}`);
    }

    generateCarousel();

    window.addEventListener('resize', () => {
        gsap.set('.story-title-container', {
            height: titleSlides[activeIndex]?.offsetHeight
        });
        gsap.set('.story-year-container', {
            height: detailSlides[activeIndex]?.offsetHeight
        });
    });

    return {
        slideUp,
        slideDown,
        slideTo
    };
};

export default createTextCarousel;
